import { computed, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { catchError, defer, finalize, map, Observable, of, shareReplay, Subject, takeUntil, tap } from 'rxjs';
import { RequestStatus } from '../../../state/latest-request';
import { SessionStateResetService } from '../../../state/session-state-reset.service';
import { UserGroup, UserService } from '../../admins/user.service';

export const ArReportsAccessStore = signalStore(
  { providedIn: 'root' },
  withState(() => ({ access: null as boolean | null, request: { loading: false, error: null } as RequestStatus })),
  withComputed((store) => ({ hasAccess: computed(() => store.access() === true) })),
  withMethods((store, users = inject(UserService), destroyRef = inject(DestroyRef)) => {
    let pending: Observable<boolean> | null = null;
    const reset$ = new Subject<void>();
    return {
      canAccess(): Observable<boolean> {
        const access = store.access();
        if (access !== null) return of(access);
        if (!pending) {
          pending = defer(() => {
            patchState(store, { request: { loading: true, error: null } });
            return users.bootstrapCurrentUser();
          }).pipe(
            map((user) => user?.group === UserGroup.SuperAdmin || user?.featureAccess?.arReports === true),
            tap((access) => patchState(store, { access })),
            catchError(() => {
              patchState(store, { request: { loading: false, error: 'Unable to verify AR Reports access.' } });
              // Fail closed without permanently caching a transient failure.
              return of(false);
            }),
            takeUntil(reset$),
            takeUntilDestroyed(destroyRef),
            finalize(() => {
              pending = null;
              patchState(store, { request: { ...store.request(), loading: false } });
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
          );
        }
        return pending;
      },
      reset(): void {
        reset$.next();
        pending = null;
        patchState(store, { access: null, request: { loading: false, error: null } });
      },
    };
  }),
  withHooks({
    onInit(store) {
      const unregister = inject(SessionStateResetService).register(() => store.reset());
      inject(DestroyRef).onDestroy(unregister);
    },
  }),
);
